import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/store/auth.store';
import { useTenantStore } from '@/store/tenant.store';
import { api } from '@/services/api';

interface IncomingShare {
  id: string;
  sender_tenant_name: string;
  incident_name: string;
  status: string;
}

/** Shape of a row from GET /incident-sharing/pending (bare array, no envelope). */
interface PendingShareRow {
  id?: string;
  status?: string;
  source_tenant_name?: string;
  source_incident_name?: string;
}

/**
 * How often to look for newly-arrived incident shares. Matches the notification
 * bell's cadence (VITE_NOTIFICATION_POLL_INTERVAL, 30s) order of magnitude; a
 * share has a 72h default expiry so a minute of latency is immaterial.
 */
const POLL_INTERVAL_MS = 60_000;

/**
 * Announce mutual-aid incident shares that a partner tenant has sent to us.
 *
 * WHY THIS POLLS INSTEAD OF LISTENING ON A SOCKET (do not "restore" the socket):
 * this hook used to open its own connection to the default Socket.io namespace
 * and wait for a `share:incoming` event. NOTHING IN THE BACKEND HAS EVER EMITTED
 * THAT EVENT — `shareIncident()` inserts the `incident_shares` row and returns —
 * so the "New Incident Share" toast (with its Accept/Decline actions) never once
 * fired. During a regional event the receiving EOC got no announcement of any
 * kind: the share sat in `pending` until somebody happened to open Mutual Aid,
 * and otherwise silently expired after 72h, taking the partner's tasks, damage
 * assessments and board posts with it.
 *
 * Polling the existing `/incident-sharing/pending` endpoint is also strictly
 * more reliable than an emit would be: a socket broadcast only reaches browsers
 * that are connected at the instant of the share, whereas a poll re-announces on
 * the next app load — which is exactly the "share arrived while the EOC was
 * unattended" case in the original report.
 *
 * The old socket also leaked across tenants: the module-level socket was reused
 * whenever it was still connected, but the server pins a socket's tenant from
 * its handshake `auth.tenantId`, so after a tenant switch the client kept
 * receiving the PREVIOUS tenant's events. Everything here is scoped to the
 * effect and keyed on the active tenant id, so a switch tears the poll down and
 * starts a clean one.
 */
export function useShareNotifications(onShareReceived: (share: IncomingShare) => void) {
  const token = useAuthStore((s) => s.token);
  const canViewIncidents = useAuthStore(
    (s) =>
      !!s.user?.is_platform_admin ||
      s.permissions.some((p) => p.module === 'incidents' && p.action === 'view'),
  );
  const { activeTenant } = useTenantStore();
  const tenantId = activeTenant?.id;

  // Callers pass an inline arrow, so the callback identity changes on every
  // render. Keep it in a ref: if it were an effect dependency the poll (and the
  // already-announced set below) would be torn down and rebuilt each render and
  // the same share would be toasted over and over.
  const onShareReceivedRef = useRef(onShareReceived);
  useEffect(() => {
    onShareReceivedRef.current = onShareReceived;
  }, [onShareReceived]);

  useEffect(() => {
    if (!token || !tenantId || !canViewIncidents) return;

    // Scoped to this tenant: a tenant switch rebuilds the effect and therefore
    // re-announces whatever is pending for the tenant now in context.
    const announced = new Set<string>();
    let cancelled = false;

    const poll = async () => {
      try {
        const shares = await api.get<PendingShareRow[]>('/incident-sharing/pending');
        if (cancelled || !Array.isArray(shares)) return;

        for (const share of shares) {
          if (!share?.id || announced.has(share.id)) continue;
          announced.add(share.id);
          onShareReceivedRef.current({
            id: share.id,
            sender_tenant_name: share.source_tenant_name || 'A partner organization',
            incident_name: share.source_incident_name || 'an incident',
            status: share.status || 'pending',
          });
        }
      } catch {
        // Best-effort: offline, a transient 5xx, or a permission change should
        // never surface an error here — the next tick retries.
      }
    };

    void poll();
    const timer = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [token, tenantId, canViewIncidents]);
}
